import React, { useId, useRef } from 'react'

import {
  Button,
  Icon,
  Upload,
  InfoOutlined,
  DeleteOutlined,
  Replace,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { func, object, bool } from 'prop-types'

import useUseCaseTranslate from 'Pods/LazyLoaded/UseCaseAreaSidebar/hooks/useUseCaseTranslate'
import useSentry from 'Pods/Shared/Hooks/useSentry'

import {
  SLICER_ACCEPTED_FILE_TYPES,
  SLICER_MAX_FILE_SIZE,
  formatBytes,
  getFileExtension,
  getFileNameWithoutExtension,
  validateSlicerFile,
} from '../../../../Utils/fileValidation'

function SlicerFileUploadSection({
  slicerFile = null,
  onSelect,
  onRemove,
  disabled = false,
  onShowNotification,
}) {
  const fileInputRef = useRef(null)
  const hintId = useId()
  const { captureException } = useSentry()
  const { t } = useUseCaseTranslate()

  const handleValidation = (file) => {
    const { valid, error, errorType } = validateSlicerFile(file, t)

    if (valid) return true

    onShowNotification(error, 'error')

    if (errorType === 'size') {
      captureException(new Error('Slicer file size exceeded'), {
        tags: {
          component: 'use_case',
          action: 'file_size_exceeded',
        },
        contexts: {
          file_upload: {
            fileName: file.name,
            fileSize: file.size,
            maxSize: SLICER_MAX_FILE_SIZE,
            fileType: file.type,
            context: 'slicer_upload',
          },
        },
      })
    }

    return false
  }

  const handleFileChange = (event) => {
    const files = event.target.files
    const input = fileInputRef.current

    if (files && files.length > 0) {
      const file = files[0]

      if (handleValidation(file)) {
        onSelect([file])
      }

      if (input) {
        input.value = ''
      }
    }
  }

  const handleBrowseClick = () => {
    if (fileInputRef.current && !disabled) {
      fileInputRef.current.click()
    }
  }

  const handleRemoveClick = () => {
    onRemove()
  }

  return (
    <div className="use-cases-area__slicer-upload">
      <input
        ref={fileInputRef}
        type="file"
        accept={SLICER_ACCEPTED_FILE_TYPES}
        onChange={handleFileChange}
        disabled={disabled}
        style={{ display: 'none' }}
        aria-label={t('form.slicerUpload')}
        aria-describedby={hintId}
      />
      {!slicerFile ? (
        <>
          <Button
            type="secondary"
            onClick={handleBrowseClick}
            disabled={disabled}
            customClass="use-cases-area__file-upload-button"
            variant="with-icon"
            size="small"
            expand
            icon={<Icon svgComponent={Upload} size="small" color="dark" />}
            aria-describedby={hintId}
          >
            {t('form.slicerUpload')}
          </Button>
          <div className="use-cases-area__slicer-upload-hint">
            <Icon svgComponent={InfoOutlined} size="small" color="dark" />
            <span id={hintId} className="cgt-caption">
              {t('form.slicerHint')}
            </span>
          </div>
        </>
      ) : (
        <div className="use-cases-area__slicer-file-card">
          <div className="use-cases-area__slicer-file-preview">
            <div className="use-cases-area__slicer-file-badge">
              <span className="use-cases-area__slicer-file-extension cgt-legacy-text-caption">
                {getFileExtension(slicerFile.name)}
              </span>
            </div>
            <div className="use-cases-area__slicer-file-info">
              <span className="use-cases-area__slicer-file-name cgt-text-medium">
                {getFileNameWithoutExtension(slicerFile.name)}
              </span>
              <span className="use-cases-area__slicer-file-size cgt-legacy-text-caption">
                {formatBytes(slicerFile.size)}
              </span>
            </div>
          </div>
          <div className="use-cases-area__slicer-file-actions">
            <Button
              type="secondary"
              onClick={handleBrowseClick}
              disabled={disabled}
              size="small"
              customClass="use-cases-area__slicer-action-button"
            >
              <span className="use-cases-area__slicer-action-content cgt-body-base-semibold">
                <Icon svgComponent={Replace} size="small" color="dark" />
                {t('form.replace')}
              </span>
            </Button>
            <Button
              type="secondary"
              onClick={handleRemoveClick}
              size="small"
              customClass="use-cases-area__slicer-action-button"
            >
              <span className="use-cases-area__slicer-action-content cgt-body-base-semibold">
                <Icon svgComponent={DeleteOutlined} size="small" color="dark" />
                {t('form.delete')}
              </span>
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

SlicerFileUploadSection.propTypes = {
  slicerFile: object,
  onSelect: func.isRequired,
  onRemove: func.isRequired,
  disabled: bool,
  onShowNotification: func.isRequired,
}

export default SlicerFileUploadSection
